import { ArrowRightArrowLeft, LayoutHeaderCellsLargeFill } from '@gravity-ui/icons';
import type { Nullable } from '@silverhand/essentials';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { type ReactNode, useContext, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import DynamicT from '@/shared/components/DynamicT';
import { isLightLogoInDark, lightLogoBackdropClass } from '@/shared/utils/logo';

export type Props = {
  readonly className?: string;
  readonly logo?: Nullable<string>;
  readonly thirdPartyLogo?: Nullable<string>;
  readonly headline?: TFuncKey;
  readonly headlineInterpolation?: Record<string, unknown>;
  readonly description?: ReactNode;
};

const BrandingHeader = ({
  logo,
  thirdPartyLogo,
  headline,
  headlineInterpolation,
  description,
  className,
}: Props) => {
  const { theme, experienceSettings } = useContext(PageContext);
  /**
   * The third-party logo is a client-declared remote asset (e.g. a CIMD `logo_uri`): `no-referrer`
   * keeps the tenant origin from leaking to that host, and a broken image falls back to a generic
   * app glyph so the header keeps its shape.
   */
  const [isThirdPartyLogoBroken, setIsThirdPartyLogoBroken] = useState(false);
  const isConnectPair = Boolean(thirdPartyLogo && logo);
  const shouldShowLogo = Boolean(thirdPartyLogo ?? logo);

  return (
    <header
      className={classNames(
        'flex w-full flex-col',
        isConnectPair ? 'items-center text-center' : 'items-start text-start',
        className
      )}
    >
      {shouldShowLogo && (
        <div className="flex items-center gap-3 not-last:mb-6">
          {thirdPartyLogo &&
            (isThirdPartyLogoBroken ? (
              <span className="grid size-8 place-items-center rounded-lg bg-default text-muted">
                <LayoutHeaderCellsLargeFill className="size-4" />
              </span>
            ) : (
              <img
                className="h-8 w-auto max-w-40 object-contain"
                alt="third party logo"
                src={thirdPartyLogo}
                referrerPolicy="no-referrer"
                onError={() => {
                  setIsThirdPartyLogoBroken(true);
                }}
              />
            ))}
          {isConnectPair && <ArrowRightArrowLeft aria-hidden className="size-4 text-muted" />}
          {logo && (
            <img
              className={classNames(
                'h-8 w-auto max-w-40 object-contain',
                isLightLogoInDark(theme, experienceSettings?.branding, logo) &&
                  lightLogoBackdropClass
              )}
              alt="app logo"
              src={logo}
            />
          )}
        </div>
      )}
      {headline && (
        <h1
          tabIndex={-1}
          className="page-title text-2xl/7 font-semibold tracking-tight text-foreground wrap-anywhere outline-none"
        >
          <DynamicT isNumberedWordKept forKey={headline} interpolation={headlineInterpolation} />
        </h1>
      )}
      {description && (
        <div className="page-description mt-2 text-sm text-pretty text-muted">{description}</div>
      )}
    </header>
  );
};

export default BrandingHeader;
